import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { NgTemplateOutlet } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { VisualizadorPdf } from '@/shared/components/visualizador-pdf/visualizador-pdf';

import type { MedicoPCSMODTO } from '@/core/api/sicom-api/servicos/pessoa-fisica/pcmso/medico-pcmso.model';
import { PcmsoServicosService } from '@/features/servicos/pessoa-fisica/content/pcmso-servicos/pcmso-servicos.service';
import { formatarDataHoraMascara, formatarDataMascara, montarRequestProtocolo, valorTexto, type ProtocoloResumoConsulta } from '@/features/servicos/shared/utils/protocolo-consulta.utils';
import { iconeDaSecao, secaoEhDeDados, subtituloDaSecao } from '@/features/servicos/shared/utils/secao-detalhamento.utils';
import { rotuloBadgeSituacao, situacaoClassePorTexto } from '@/features/servicos/shared/utils/protocolo-rotulos.utils';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface CampoVisualizacao {
    label: string;
    chave: keyof MedicoPCSMODTO;
    formatador?: (valor: unknown) => string;
}

interface SecaoVisualizacao {
    titulo: string;
    campos: CampoVisualizacao[];
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-pcmso-servicos',
    standalone: true,
    imports: [DialogoPadraoDirective, NgTemplateOutlet, CardContent, DialogModule, VisualizadorPdf, InputTextModule],
    templateUrl: './pcmso-servicos.html',
    styleUrl: './pcmso-servicos.scss'
})
export class PcmsoServicos {
    /**
     * Apresentação das seções — ícone, subtítulo e se a seção é de dados — vem do vocabulário
     * compartilhado: o mesmo título tem a mesma cara em todos os detalhamentos.
     */
    protected readonly iconeDaSecao = iconeDaSecao;
    protected readonly subtituloDaSecao = subtituloDaSecao;
    protected readonly secaoEhDeDados = secaoEhDeDados;
    protected readonly situacaoClassePorTexto = situacaoClassePorTexto;
    protected readonly rotuloBadgeSituacao = rotuloBadgeSituacao;

    readonly protocoloSelecionado = input<ProtocoloResumoConsulta | null>(null);
    readonly codigoEstado = input('');

    readonly carregando = signal(false);
    readonly erroConsulta = signal<string | null>(null);
    readonly dados = signal<MedicoPCSMODTO | null>(null);
    readonly exibirDialogAnexo = signal(false);
    readonly blobAnexo = signal<Blob | null>(null);
    readonly nomeArquivoAnexo = signal('anexo-pcmso.pdf');

    readonly secoes: SecaoVisualizacao[] = [
        {
            titulo: 'Protocolo',
            campos: [
                { label: 'Número Protocolo', chave: 'numeroProtocolo' },
                { label: 'Ano Protocolo', chave: 'anoProtocolo' },
                { label: 'Data/Hora Solicitacao', chave: 'dataHora', formatador: formatarDataHoraMascara },
                { label: 'Situação', chave: 'situacao', formatador: this.formatarSituacao }
            ]
        },
        {
            titulo: 'Dados do Solicitante',
            campos: [
                { label: 'Nome', chave: 'nome' },
                { label: 'CPF', chave: 'cpf' },
                { label: 'CRM', chave: 'numCrm' },
                { label: 'UF CRM Origem', chave: 'ufCrmOrigem' },
                { label: 'Email Solicitante', chave: 'emailSolicitante' },
                { label: 'Email Empresa', chave: 'emailEmpresa' },
                { label: 'Razão Social', chave: 'razaoSocial' },
                { label: 'CNPJ', chave: 'cnpj' }
            ]
        },
        {
            titulo: 'Dados do Programa',
            campos: [
                { label: 'Qtd Funcionários', chave: 'qtd_funcionario' },
                { label: 'Classificação de Risco', chave: 'risco', formatador: this.formatarRisco },
                { label: 'Divisao / Area / Setor', chave: 'area' },
                { label: 'Início do Programa', chave: 'dataInicioPrograma', formatador: formatarDataMascara },
                { label: 'Fim do Programa', chave: 'dataFimPrograma', formatador: formatarDataMascara }
            ]
        }
    ];

    private readonly destroyRef = inject(DestroyRef);
    private ultimaChaveConsulta = '';

    private readonly servico = inject(PcmsoServicosService);
    private readonly messageService = inject(MessageService);

    constructor() {
        // Recarrega ao mudar protocolo/UF; o guard ultimaChaveConsulta evita repetir a mesma consulta.
        effect(() => {
            this.protocoloSelecionado();
            this.codigoEstado();
            this.carregarDados();
        });
    }

    /**
     * Campo que sai como selo colorido, e não como valor comum: só a situação do protocolo. Estava
     * repetido em duas condições idênticas no template (a classe do bloco e o `@if` do conteúdo), e
     * bastava uma delas mudar para o selo aparecer sem a cor — ou a cor sem o selo.
     */
    ehCampoBadge(campo: CampoVisualizacao): boolean {
        const label = campo.label.toLowerCase();
        return campo.chave === 'situacao' || label.includes('situacao') || label.includes('situação');
    }

    /**
     * Id do controle de um campo, para o `<label for>` apontar para ele. O id junta seção e rótulo
     * porque os rótulos se repetem entre seções — só o rótulo geraria ids duplicados, e o clique no
     * label levaria o foco para o campo da outra seção.
     */
    idDoCampo(secao: SecaoVisualizacao, campo: CampoVisualizacao): string {
        return `pcmso-${this.emSlug(secao.titulo)}-${this.emSlug(campo.label)}`;
    }

    /** Texto reduzido a `a-z0-9-`, para servir de id de elemento. */
    private emSlug(texto: string): string {
        return texto
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    }

    valorCampo(campo: CampoVisualizacao): string {
        const dados = this.dados();
        if (!dados) {
            return '-';
        }

        const valorBruto = dados[campo.chave];
        if (valorBruto === null || valorBruto === undefined || valorTexto(valorBruto).trim() === '') {
            return '-';
        }

        if (campo.formatador) {
            const valorFormatado = campo.formatador(valorBruto);
            return valorFormatado ? valorFormatado : '-';
        }

        return valorTexto(valorBruto);
    }

    possuiAnexo(): boolean {
        return !!valorTexto(this.dados()?.linkAnexo).trim();
    }

    onVisualizarAnexo(event: Event): void {
        event.preventDefault();
        this.visualizarAnexoPorCampo(this.dados()?.linkAnexo);
    }

    fecharDialogAnexo(): void {
        this.exibirDialogAnexo.set(false);
        this.blobAnexo.set(null);
    }

    private carregarDados(): void {
        const request = montarRequestProtocolo(this.protocoloSelecionado(), this.codigoEstado());

        if (!request) {
            this.ultimaChaveConsulta = '';
            this.dados.set(null);
            this.carregando.set(false);
            this.erroConsulta.set(null);
            return;
        }

        const chaveConsulta = `${request.numeroProtocolo}/${request.anoProtocolo}-${request.ufCrm}`;
        if (chaveConsulta === this.ultimaChaveConsulta) {
            return;
        }

        this.ultimaChaveConsulta = chaveConsulta;
        this.carregando.set(true);
        this.erroConsulta.set(null);

        this.servico
            .consultar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.dados.set(response ?? null);
                    this.carregando.set(false);
                },
                error: () => {
                    this.dados.set(null);
                    this.carregando.set(false);
                    this.erroConsulta.set('Falha ao carregar os dados de PCMSO.');
                }
            });
    }

    private visualizarAnexoPorCampo(campoAnexo: unknown): void {
        const idAnexo = this.extrairIdAnexo(campoAnexo);
        if (!idAnexo) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Id do anexo não identificado.' });
            return;
        }

        this.servico
            .visualizarAnexo(idAnexo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (blob) => {
                    this.blobAnexo.set(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
                    this.exibirDialogAnexo.set(true);
                },
                error: () => {
                    // Mensagem fica a cargo do interceptor central; aqui so o estado local.
                    this.blobAnexo.set(null);
                    this.exibirDialogAnexo.set(false);
                }
            });
    }

    private extrairIdAnexo(campoAnexo: unknown): string | null {
        const texto = valorTexto(campoAnexo).trim();
        if (!texto) {
            return null;
        }

        const regexPorCaminho = /\/(?:webservices\/)?baixar-anexo\/([^/?#]+)/i;
        const matchDireto = regexPorCaminho.exec(texto);
        if (matchDireto?.[1]) {
            return decodeURIComponent(matchDireto[1]);
        }

        try {
            const url = new URL(texto, window.location.origin);
            const matchPathname = regexPorCaminho.exec(url.pathname);
            if (matchPathname?.[1]) {
                return decodeURIComponent(matchPathname[1]);
            }

            const idQuery = url.searchParams.get('idAnexo') || url.searchParams.get('id');
            if (idQuery?.trim()) {
                return idQuery.trim();
            }
        } catch {
            // Ignora parsing de URL e segue fallback.
        }

        if (/^[A-Za-z0-9_-]+$/.test(texto)) {
            return texto;
        }

        return null;
    }

    private formatarSituacao(valor: unknown): string {
        const codigo = valorTexto(valor).trim().toUpperCase();
        switch (codigo) {
            case 'A':
                return 'Ativo';
            case 'T':
                return 'Tramitado';
            case 'P':
                return 'Pendência';
            case 'C':
                return 'Cancelado';
            case 'E':
                return 'Encerrado';
            default:
                return valorTexto(valor);
        }
    }

    private formatarRisco(valor: unknown): string {
        const risco = valorTexto(valor).trim();
        switch (risco) {
            case '1':
                return 'Nível 1 (Risco Baixo)';
            case '2':
                return 'Nível 2 (Risco Médio)';
            case '3':
                return 'Nível 3 (Risco Alto)';
            case '4':
                return 'Nível 4 (Risco Muito Alto)';
            default:
                return risco || '-';
        }
    }
}
